import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { resolvePhotoUrl } from "@/lib/img";
import { toast } from "sonner";
import { Plus } from "@phosphor-icons/react";
import { PageHeader, fmtINR, timeAgo, Toolbar, ToolbarSelect } from "./_shared";

const STATUS_FILTER_OPTIONS = [
  { v: "all", l: "All" },
  { v: "active", l: "Active" },
  { v: "accepted", l: "Accepted" },
  { v: "rejected", l: "Rejected" },
  { v: "countered", l: "Countered" },
];

export default function LiveOffersAdmin() {
  const [offers, setOffers] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [filter, setFilter] = useState("all");

  const load = async () => {
    try {
      const [o, v] = await Promise.all([
        api.get("/admin/live-offers"),
        api.get("/admin/seller-leads"),
      ]);
      setOffers(o.data || []);
      setVehicles((v.data || []).filter((x) => x.live_offers_enabled));
    } catch (err) {
      console.error("LiveOffersAdmin load failed:", err);
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 10000);
    return () => clearInterval(t);
  }, []);

  const filtered = useMemo(
    () => offers.filter((o) => filter === "all" || (o.status || "active") === filter),
    [offers, filter]
  );

  const byVehicle = useMemo(() => {
    const m = {};
    for (const o of offers) {
      const k = o.vehicle_id;
      if (!m[k] || m[k].amount < o.amount) m[k] = o;
    }
    return m;
  }, [offers]);

  const respond = async (oid, action) => {
    let counter_amount = null;
    if (action === "counter") {
      const a = prompt("Counter amount (₹):");
      if (!a) return;
      counter_amount = parseInt(a, 10);
      if (Number.isNaN(counter_amount) || counter_amount <= 0) {
        toast.error("Invalid counter amount");
        return;
      }
    }
    try {
      await api.post(`/admin/offers/${oid}/respond`, { action, counter_amount });
      toast.success(`Offer ${action}`);
      load();
    } catch (err) {
      console.error("Offer respond failed:", err);
      toast.error("Failed to update offer");
    }
  };

  return (
    <div data-testid="admin-live-offers">
      <PageHeader title="Negotiation Cockpit" subtitle="Active Acquisitions" />

      <InjectionCockpit vehicles={vehicles} byVehicle={byVehicle} onInjected={load} />

      <Toolbar>
        <ToolbarSelect value={filter} onChange={setFilter} testid="offers-filter" options={STATUS_FILTER_OPTIONS} />
        <div className="font-mono-data text-[10px] tracking-widest text-white/40 ml-auto">
          {filtered.length} offers · {Object.keys(byVehicle).length} vehicles · auto-refresh 10s
        </div>
      </Toolbar>

      <div className="qd-card overflow-x-auto">
        <table className="term-table w-full">
          <thead><tr>
            <th>Time</th><th>Vehicle</th><th>Source</th><th className="text-right">Amount</th><th>Status</th><th>Current?</th><th>Actions</th>
          </tr></thead>
          <tbody>
            {filtered.map((o) => {
              const isHigh = byVehicle[o.vehicle_id]?.id === o.id;
              const status = o.status || "active";
              return (
                <tr key={o.id} data-testid={`offer-row-${o.id}`}>
                  <td className="text-white/55">{timeAgo(o.created_at)}</td>
                  <td className="text-white/70 text-[10px]">{o.vehicle_id.slice(0, 8)}…</td>
                  <td className="text-white">{o.buyer_label}</td>
                  <td className="text-right text-[#FF1E1E] font-bold">{fmtINR(o.amount)}</td>
                  <td>
                    <span className="text-[10px] font-mono-data tracking-widest uppercase" style={{
                      color: status === "accepted" ? "#22C55E" : status === "rejected" ? "#808080" : status === "countered" ? "#F5A623" : "#FF1E1E"
                    }}>{status}</span>
                  </td>
                  <td>{isHigh ? <span className="text-[10px] font-display tracking-widest text-[#FF1E1E]">HIGH</span> : ""}</td>
                  <td className="flex gap-1 py-2">
                    <button onClick={() => respond(o.id, "accept")} data-testid={`accept-${o.id}`} className="text-[10px] font-display tracking-widest px-2 py-1 border border-green-500/40 text-green-400 hover:bg-green-500/10">ACCEPT</button>
                    <button onClick={() => respond(o.id, "counter")} data-testid={`counter-${o.id}`} className="text-[10px] font-display tracking-widest px-2 py-1 border border-[#F5A623]/40 text-[#F5A623] hover:bg-[#F5A623]/10">COUNTER</button>
                    <button onClick={() => respond(o.id, "reject")} data-testid={`reject-${o.id}`} className="text-[10px] font-display tracking-widest px-2 py-1 border border-[#808080]/40 text-white/60 hover:bg-white/5">REJECT</button>
                  </td>
                </tr>
              );
            })}
            {filtered.length === 0 && <tr><td colSpan={7} className="text-center text-white/40 py-8">No bids match filter</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function InjectionCockpit({ vehicles, byVehicle, onInjected }) {
  if (vehicles.length === 0) return null;
  return (
    <div className="qd-card mb-5 overflow-hidden" data-testid="injection-cockpit">
      <div className="px-4 py-3 border-b border-[#1A1A1A] flex items-center justify-between flex-wrap gap-2">
        <div className="qd-overline">Send Dealership Offer</div>
        <div className="font-mono-data text-[10px] tracking-widest text-white/40">
          {vehicles.length} active acquisition{vehicles.length === 1 ? "" : "s"}
        </div>
      </div>
      <div className="divide-y divide-[#1A1A1A]">
        {vehicles.map((v) => (
          <InjectionRow
            key={v.id}
            vehicle={v}
            highest={byVehicle[v.id]?.amount || 0}
            onInjected={onInjected}
          />
        ))}
      </div>
    </div>
  );
}

function InjectionRow({ vehicle, highest, onInjected }) {
  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState(false);
  const v = vehicle;

  const inject = async () => {
    const n = parseInt(amount.replace(/\D/g, ""), 10);
    if (!n || n <= 0) {
      toast.error("Enter a valid amount");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/admin/offers/${v.id}`, { amount: n });
      toast.success(`Dealership offer sent · ${fmtINR(n)}`);
      setAmount("");
      onInjected();
    } catch (err) {
      console.error("Send offer failed:", err);
      toast.error(err.response?.data?.detail || "Failed to send offer");
    } finally { setBusy(false); }
  };

  return (
    <div className="px-4 py-3 grid grid-cols-12 gap-3 items-center" data-testid={`inject-row-${v.id}`}>
      <div className="col-span-12 md:col-span-6 flex items-center gap-3 min-w-0">
        {(v.photos_signed?.[0]?.url || v.photos?.[0]) ? (
          <div className="w-12 h-12 shrink-0 bg-[#0E0E0E] overflow-hidden">
            <img
              src={resolvePhotoUrl(v.photos_signed?.[0] || v.photos[0])}
              alt=""
              className="w-full h-full object-cover"
              loading="lazy"
              onError={(e) => { e.currentTarget.style.display = "none"; }}
            />
          </div>
        ) : (
          <div className="w-12 h-12 shrink-0 bg-[#0E0E0E] border border-[#1F1F1F] flex items-center justify-center text-[9px] text-white/30">no img</div>
        )}
        <div className="min-w-0">
          <div className="font-display text-sm text-white truncate">{v.make} {v.model}</div>
          <div className="text-[10px] text-white/45 tracking-wide truncate">
            {v.registration_number} · expected {fmtINR(v.expected_price)}{v.current_admin_offer ? ` · current ${fmtINR(v.current_admin_offer)}` : ""}
          </div>
        </div>
      </div>
      <div className="col-span-9 md:col-span-4">
        <input
          data-testid={`inject-amount-${v.id}`}
          value={amount}
          onChange={(e) => setAmount(e.target.value.replace(/[^\d]/g, ""))}
          onKeyDown={(e) => { if (e.key === "Enter") inject(); }}
          inputMode="numeric"
          placeholder="Dealership offer ₹"
          className="w-full bg-[#0F0F0F] border border-[#2A2A2A] text-white text-xs px-3 py-2 outline-none focus:border-[#FF1E1E]"
        />
      </div>
      <div className="col-span-3 md:col-span-2">
        <button
          data-testid={`inject-submit-${v.id}`}
          onClick={inject}
          disabled={busy || !amount}
          className="w-full btn-primary text-xs py-2 inline-flex items-center justify-center gap-1.5"
        >
          <Plus size={12} weight="bold" /> {busy ? "…" : "Send"}
        </button>
      </div>
    </div>
  );
}
